import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { useExchangeRates } from "@/hooks/useExchangeRates";
import { formatInCurrency, formatEquivalents } from "@/lib/currencyConversion";
import {
  extractSnapshotHotels,
  snapshotNights,
  quotePaxCount,
  quoteDisplayLabel,
  quoteOptionalExtras,
  type SnapshotHotel,
  type OptionalExtra,
  perPersonBasisLabel,
} from "@/lib/quoteLabelUtils";

export interface ComparisonColumn {
  quote: any;
  option: { id?: string; label?: string; quantity?: number } | null;
  pax: number | null;
  total: number;
  perPerson: number | null;
  nights: number;
  hotels: SnapshotHotel[];
  extras: OptionalExtra[];
  supplement: number;
  label: string;
  perPersonLabel: string;
}

/** Derive the comparison columns for a set of quotes (shared by every comparison surface). */
export function buildComparisonColumns(
  quotes: any[],
  opts?: {
    participantOptions?: { id: string; label: string; quantity: number }[];
    lead?: any;
  }
): ComparisonColumn[] {
  return (quotes || []).map((quote) => {
    const option =
      opts?.participantOptions?.find((o) => o.id === quote?.participant_option_id) ||
      (quote?.option_quantity
        ? { label: quote.option_label, quantity: Number(quote.option_quantity) }
        : null);
    const pax = quotePaxCount(quote, option, opts?.lead);
    const total = Number(quote?.total_amount) || 0;
    return {
      quote,
      option,
      pax,
      total,
      perPerson: pax ? total / pax : null,
      nights: snapshotNights(quote, opts?.lead),
      hotels: extractSnapshotHotels(quote?.itinerary_snapshot),
      extras: quoteOptionalExtras(quote),
      supplement: Number(quote?.single_supplement_amount) || 0,
      perPersonLabel: perPersonBasisLabel(
        Array.isArray(quote?.itinerary_snapshot) ? quote.itinerary_snapshot : quote?.itinerary_snapshot?.items
      ),
      label: quoteDisplayLabel(quote, { option, lead: opts?.lead, includeNumber: false }),
    };
  });
}

interface MoneyProps {
  amount: number;
  currency?: string;
  className?: string;
  equivalentsClassName?: string;
  inline?: boolean;
}

/** A price with its dollar and euro equivalents beneath (or alongside when inline). */
export function MoneyWithEquivalents({
  amount,
  currency = "ZAR",
  className,
  equivalentsClassName,
  inline = false,
}: MoneyProps) {
  const { data: rates } = useExchangeRates();
  const equivalents = amount > 0 ? formatEquivalents(amount, currency, rates) : null;
  const base = formatInCurrency(amount, currency);

  if (inline) {
    return (
      <span className={className}>
        {base}
        {equivalents && (
          <span className={cn("text-muted-foreground ml-1", equivalentsClassName || "text-xs")}>
            ({equivalents})
          </span>
        )}
      </span>
    );
  }

  return (
    <span className={cn("inline-block", className)}>
      <span className="block">{base}</span>
      {equivalents && (
        <span
          className={cn(
            "block font-normal text-muted-foreground",
            equivalentsClassName || "text-xs"
          )}
        >
          {equivalents}
        </span>
      )}
    </span>
  );
}

interface QuoteComparisonCardsProps {
  quotes: any[];
  lead?: any;
  currency?: string;
  participantOptions?: { id: string; label: string; quantity: number }[];
  /** Where "View full programme" points; defaults to the public quote link. */
  quoteHref?: (quote: any) => string | null;
}

/**
 * Client-safe side-by-side comparison cards. Used by the public comparison page and
 * the client dashboard so both show identical information.
 */
export function QuoteComparisonCards({
  quotes,
  lead,
  currency = "ZAR",
  participantOptions,
  quoteHref,
}: QuoteComparisonCardsProps) {
  const columns = useMemo(
    () => buildComparisonColumns(quotes, { participantOptions, lead }),
    [quotes, participantOptions, lead]
  );

  const href = (quote: any) =>
    quoteHref ? quoteHref(quote) : quote?.share_token ? `/quote/${quote.share_token}` : null;

  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {columns.map((c) => (
        <div key={c.quote.id} className="rounded-lg border p-5 flex flex-col bg-card">
          <h3 className="font-semibold text-base">{c.label}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Reference {c.quote.quote_number}
          </p>

          <div className="mt-4">
            <MoneyWithEquivalents
              amount={c.total}
              currency={currency}
              className="text-2xl font-semibold"
              equivalentsClassName="text-xs"
            />
            {c.perPerson != null && (
              <div className="text-sm text-muted-foreground mt-1">
                <MoneyWithEquivalents
                  amount={c.perPerson}
                  currency={currency}
                  inline
                  className="font-medium text-foreground"
                />{" "}
                {c.perPersonLabel}
              </div>
            )}
            <div className="text-xs text-muted-foreground mt-1">
              {[c.pax ? `${c.pax} guests` : null, c.nights > 0 ? `${c.nights} nights` : null]
                .filter(Boolean)
                .join(" · ")}
            </div>
          </div>

          {c.hotels.length > 0 && (
            <div className="mt-4">
              <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">
                Accommodation
              </h4>
              <ul className="space-y-1 text-sm">
                {c.hotels.map((h, i) => (
                  <li key={i}>
                    <span className="font-medium">{h.name}</span>
                    <span className="text-muted-foreground">
                      {h.city ? ` · ${h.city}` : ""}
                      {h.nights > 0 ? ` · ${h.nights} night${h.nights === 1 ? "" : "s"}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {c.supplement > 0 && (
            <div className="mt-4 text-sm">
              <span className="text-muted-foreground">Single supplement: </span>
              <MoneyWithEquivalents
                amount={c.supplement}
                currency={currency}
                className="font-medium"
              />
            </div>
          )}

          {c.extras.length > 0 && (
            <div className="mt-4">
              <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">
                Optional extras
              </h4>
              <ul className="space-y-1.5 text-sm">
                {c.extras.map((e, i) => (
                  <li key={i}>
                    {e.description}
                    <MoneyWithEquivalents
                      amount={e.price_per_person}
                      currency={currency}
                      className="block font-medium"
                    />
                    <span className="text-xs text-muted-foreground">per person</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {href(c.quote) && (
            <a
              href={href(c.quote)!}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground text-sm font-medium h-9 px-4 hover:opacity-90"
            >
              View full programme
            </a>
          )}
        </div>
      ))}
    </div>
  );
}
